/**
 * Axios client for admin-service REST API.
 * Handles cookie-based auth refresh on 401 and success toasts from API envelopes.
 */

import axios from 'axios';
import { getAdminApiBaseUrl } from '../config/adminApi';
import { showSuccessToast } from '../lib/toast';

const baseURL = getAdminApiBaseUrl();

/** Shared axios instance (credentials + JSON). */
export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Single in-flight refresh promise to dedupe concurrent 401 retries. */
let refreshPromise = null;

/**
 * @param {import('axios').InternalAxiosRequestConfig | undefined} config
 * @returns {boolean}
 */
function isAuthRefreshRequest(config) {
  const url = config?.url || '';
  return url.includes('/v1/admin/auth/refresh');
}

/**
 * @param {import('axios').InternalAxiosRequestConfig | undefined} config
 * @returns {boolean}
 */
function isAuthLoginRequest(config) {
  const url = config?.url || '';
  return url.includes('/v1/admin/auth/login');
}

const SILENT_SUCCESS_URL_PARTS = ['/v1/admin/auth/refresh'];

/**
 * Whether to surface `body.message` as a success toast after a successful response.
 * @param {import('axios').InternalAxiosRequestConfig | undefined} config
 * @param {Record<string, unknown> | undefined} body
 * @returns {boolean}
 */
function shouldShowSuccessToast(config, body) {
  if (!body || body.success !== true) return false;
  const message = body.message;
  if (typeof message !== 'string' || !message.trim()) return false;
  const url = String(config?.url || '');
  return !SILENT_SUCCESS_URL_PARTS.some((part) => url.includes(part));
}

api.interceptors.response.use(
  (response) => {
    if (shouldShowSuccessToast(response.config, response.data)) {
      showSuccessToast(response.data.message);
    }
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const original = error.config;
    if (status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }
    if (isAuthRefreshRequest(original) || isAuthLoginRequest(original)) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = api
          .post('/v1/admin/auth/refresh')
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
      return api(original);
    } catch {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
      return Promise.reject(error);
    }
  }
);

/** Thrown when the API envelope has `success: false`. */
export class ApiError extends Error {
  /**
   * @param {string} [message]
   * @param {string} [code]
   * @param {Record<string, string[]> | undefined} [fieldErrors]
   */
  constructor(message, code, fieldErrors) {
    super(message || 'Request failed');
    this.name = 'ApiError';
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Unwrap `{ success, data, error }` admin API responses; throws {@link ApiError} on failure.
 * @param {unknown} body
 * @returns {unknown}
 */
export function unwrap(body) {
  if (!body || typeof body !== 'object') return body;
  if (body.success === false) {
    const err = body.error || {};
    throw new ApiError(err.message, err.code, err.fieldErrors);
  }
  if (Object.prototype.hasOwnProperty.call(body, 'data')) {
    return body.data;
  }
  return body;
}
